import path from "path";
import fs from "fs-extra";
import { fileURLToPath } from "url";

// ESM/CJS compatible __dirname
const _dirName = typeof __dirname !== "undefined"
  ? __dirname
  : path.dirname(fileURLToPath(import.meta.url));
import { parseMockModule, writeMockFile, saveMockData, buildMockIndex, type MockFileConfig } from "./mockFileUtils";
import { toPosixPath, sendJson, readBody } from "./utils";
import type { IncomingMessage, ServerResponse } from "http";
import type { AutomockPluginOptions, InspectorOptions } from "./types";

interface InspectorContext {
  req: IncomingMessage;
  res: ServerResponse;
  mockDir: string;
  inspectorRoute: string;
  apiPrefix: string;
  inspectorConfig: Required<InspectorOptions>;
  getMockFileMap: () => Map<string, string>;
}

interface InspectorHandlerOptions {
  inspector: AutomockPluginOptions["inspector"];
  apiPrefix: string;
  mockDir: string;
  getMockFileMap: () => Map<string, string>;
}

interface InspectorApiContext extends InspectorContext {
  pathname: string;
}

interface InspectorPayload {
  key: string;
  config?: Partial<MockFileConfig>;
  description?: string;
}

interface MockItem {
  key: string;
  file: string;
  method: string;
  path: string;
  config: MockFileConfig;
  editable: boolean;
  description?: string;
  dataText: string;
}

function buildMockItem(
  key: string,
  filePath: string,
  mockDir: string,
  info: Awaited<ReturnType<typeof parseMockModule>>,
): MockItem {
  return {
    key,
    file: path.relative(mockDir, filePath),
    method: key.split("/").pop()?.replace(/\.js$/, "") ?? "get",
    path: key.replace(/\/[^/]+\.js$/, ""),
    config: info.config,
    editable: info.serializable,
    description: info.description,
    dataText: info.dataText,
  };
}

export const DEFAULT_ROUTE = "/__mock/";

function ensureTrailingSlash(route: string): string {
  return route.endsWith("/") ? route : `${route}/`;
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

export function normalizeInspectorConfig(
  input: AutomockPluginOptions["inspector"],
): Required<InspectorOptions> {
  if (input === false || input === undefined) {
    return { route: DEFAULT_ROUTE, enableToggle: true };
  }
  if (input === true) {
    return { route: DEFAULT_ROUTE, enableToggle: true };
  }
  return {
    route: ensureTrailingSlash(input.route ?? DEFAULT_ROUTE),
    enableToggle: input.enableToggle ?? true,
  };
}

export type InspectorRequestHandler = (
  req: IncomingMessage,
  res: ServerResponse,
) => Promise<boolean>;

export function createInspectorHandler(
  options: InspectorHandlerOptions,
): InspectorRequestHandler | null {
  if (!options.inspector) {
    return null;
  }

  const inspectorConfig = normalizeInspectorConfig(options.inspector);
  const inspectorRoute = ensureTrailingSlash(inspectorConfig.route);

  return async (req, res) => {
    if (!req.url) {
      return false;
    }
    const url = new URL(req.url, "http://localhost");
    if (!url.pathname.startsWith(inspectorRoute)) {
      return false;
    }

    await handleInspectorRequest({
      req,
      res,
      mockDir: options.mockDir,
      inspectorRoute,
      apiPrefix: options.apiPrefix,
      inspectorConfig,
      getMockFileMap: options.getMockFileMap,
    });

    return true;
  };
}

async function handleInspectorRequest(
  context: InspectorContext,
): Promise<void> {
  const { req, res, inspectorRoute } = context;
  const url = new URL(req.url || inspectorRoute, "http://localhost");
  const normalizedRoute = ensureTrailingSlash(inspectorRoute);

  if (
    url.pathname === normalizedRoute.slice(0, -1) ||
    url.pathname === normalizedRoute
  ) {
    await serveInspectorHtml(context);
    return;
  }

  const relativePath = url.pathname.startsWith(normalizedRoute)
    ? url.pathname.slice(normalizedRoute.length)
    : null;

  if (relativePath && relativePath.startsWith("api/")) {
    await handleInspectorApi({ ...context, pathname: relativePath.slice(4) });
    return;
  }

  res.statusCode = 404;
  res.end("Not Found");
}

async function serveInspectorHtml({
  res,
  inspectorRoute,
  apiPrefix,
  inspectorConfig,
}: InspectorContext): Promise<void> {
  const routeJson = JSON.stringify(ensureTrailingSlash(inspectorRoute));
  const allowToggleJson = JSON.stringify(inspectorConfig.enableToggle);
  const apiPrefixEscaped = escapeHtml(apiPrefix);

  const templatePath = path.join(_dirName, "inspector-template.html");
  const template = fs.readFileSync(templatePath, "utf-8");
  const html = template
    .replace("__API_PREFIX__", apiPrefixEscaped)
    .replace("__ROUTE_JSON__", routeJson)
    .replace("__ALLOW_TOGGLE_JSON__", allowToggleJson);

  res.setHeader("Content-Type", "text/html; charset=utf-8");
  res.end(html);
}

async function handleList(
  ctx: InspectorApiContext,
  mockFileMap: Map<string, string>,
): Promise<boolean> {
  const { res, mockDir } = ctx;
  const list = await Promise.all(
    Array.from(mockFileMap.entries()).map(async ([key, filePath]) => {
      const info = await parseMockModule(filePath);
      return buildMockItem(key, filePath, mockDir, info);
    }),
  );
  sendJson(res, { mocks: list });
  return true;
}

async function handleDetail(
  ctx: InspectorApiContext,
  mockFileMap: Map<string, string>,
): Promise<boolean> {
  const { req, res, mockDir } = ctx;
  const url = new URL(req.url || "", "http://localhost");
  const key = url.searchParams.get("key");
  if (!key) {
    sendJson(res, { error: "Missing key" }, 400);
    return true;
  }
  const filePath = mockFileMap.get(key);
  if (!filePath) {
    sendJson(res, { error: "Mock not found" }, 404);
    return true;
  }
  const info = await parseMockModule(filePath);
  sendJson(res, { mock: buildMockItem(key, filePath, mockDir, info) });
  return true;
}

async function handleUpdate(
  ctx: InspectorApiContext,
  mockFileMap: Map<string, string>,
): Promise<boolean> {
  const { req, res, mockDir, inspectorConfig } = ctx;
  if (req.method !== "POST") {
    sendJson(res, { error: "Method not allowed" }, 405);
    return true;
  }
  const body = await readBody(req);
  let payload: InspectorPayload;
  try {
    payload = JSON.parse(body);
  } catch {
    sendJson(res, { error: "Invalid JSON body" }, 400);
    return true;
  }

  const { key, config, description } = payload || {};
  if (!key) {
    sendJson(res, { error: "Invalid payload: missing key" }, 400);
    return true;
  }

  const filePath = mockFileMap.get(key);
  if (!filePath) {
    sendJson(res, { error: "Mock not found" }, 404);
    return true;
  }

  const info = await parseMockModule(filePath);

  let nextConfig = info.config;
  if (config && typeof config === "object") {
    if (!inspectorConfig.enableToggle && config.enable !== undefined) {
      delete config.enable;
    }
    nextConfig = { ...info.config, ...config };
  }

  const nextDescription =
    description !== undefined ? description : info.description;

  await writeMockFile(filePath, {
    headerComment: info.headerComment,
    config: nextConfig,
    description: nextDescription,
  });

  const updatedInfo = await parseMockModule(filePath);
  sendJson(res, { mock: buildMockItem(key, filePath, mockDir, updatedInfo) });
  return true;
}

async function handleCreate(
  ctx: InspectorApiContext,
  mockFileMap: Map<string, string>,
): Promise<boolean> {
  const { req, res, mockDir, apiPrefix } = ctx;
  if (req.method !== "POST") {
    sendJson(res, { error: "Method not allowed" }, 405);
    return true;
  }
  const body = await readBody(req);
  let payload: { method: string; path: string; description?: string; data?: unknown };
  try {
    payload = JSON.parse(body);
  } catch {
    sendJson(res, { error: "Invalid JSON body" }, 400);
    return true;
  }

  const { method, path: apiPath, data } = payload || {};
  if (!method || !apiPath) {
    sendJson(res, { error: "Invalid payload: missing method or path" }, 400);
    return true;
  }

  const fullUrl = apiPrefix + apiPath;

  try {
    let dataToSave: Buffer | string;
    if (typeof data === "object" && data !== null) {
      dataToSave = JSON.stringify(data, null, 2);
    } else {
      dataToSave = typeof data === "string" ? data : "";
    }

    await saveMockData(fullUrl, method.toLowerCase(), dataToSave, mockDir, 200);

    const newMockFileMap = await buildMockIndex(mockDir);
    for (const [key, value] of newMockFileMap.entries()) {
      mockFileMap.set(key, value);
    }

    const normalizedPath = toPosixPath(apiPath);
    const key = (normalizedPath.startsWith("/") ? "" : "/") +
      normalizedPath.toLowerCase() + "/" + method.toLowerCase() + ".js";
    const filePath = mockFileMap.get(key);

    if (!filePath) {
      sendJson(res, { error: "Mock file created but not found in map" }, 500);
      return true;
    }

    const info = await parseMockModule(filePath);
    sendJson(res, {
      success: true,
      mock: buildMockItem(key, filePath, mockDir, info),
    });
    return true;
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    sendJson(res, { error: "Failed to create mock: " + errorMessage }, 500);
    return true;
  }
}

async function handleDelete(
  ctx: InspectorApiContext,
  mockFileMap: Map<string, string>,
): Promise<boolean> {
  const { req, res } = ctx;
  const url = new URL(req.url || "", "http://localhost");
  const key = url.searchParams.get("key");
  if (!key) {
    sendJson(res, { error: "Missing key" }, 400);
    return true;
  }

  const filePath = mockFileMap.get(key);
  if (!filePath) {
    sendJson(res, { error: "Mock not found" }, 404);
    return true;
  }

  try {
    const stats = await fs.stat(filePath);
    if (stats.isDirectory()) {
      await fs.remove(filePath);
      for (const [mapKey, mapPath] of mockFileMap.entries()) {
        if (mapPath.startsWith(filePath + path.sep) || mapPath === filePath) {
          mockFileMap.delete(mapKey);
        }
      }
    } else {
      await fs.unlink(filePath);
      mockFileMap.delete(key);
    }
    sendJson(res, { success: true });
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    sendJson(res, { error: "Failed to delete: " + errorMessage }, 500);
  }
  return true;
}

type ApiHandler = (ctx: InspectorApiContext, mockFileMap: Map<string, string>) => Promise<boolean>;

async function handleBatchUpdate(
  ctx: InspectorApiContext,
  mockFileMap: Map<string, string>,
): Promise<boolean> {
  const { req, res } = ctx;
  if (req.method !== "POST") {
    sendJson(res, { error: "Method not allowed" }, 405);
    return true;
  }
  const body = await readBody(req);
  let payload: { updates?: Array<{ key: string; config?: Record<string, unknown> }> };
  try {
    payload = JSON.parse(body);
  } catch {
    sendJson(res, { error: "Invalid JSON body" }, 400);
    return true;
  }

  const updates = payload?.updates;
  if (!Array.isArray(updates) || updates.length === 0) {
    sendJson(res, { error: "Invalid payload: updates must be a non-empty array" }, 400);
    return true;
  }

  let updated = 0;
  const errors: string[] = [];

  for (const item of updates) {
    const { key, config } = item;
    if (!key) continue;

    const filePath = mockFileMap.get(key);
    if (!filePath) {
      errors.push(`Mock not found: ${key}`);
      continue;
    }

    try {
      const info = await parseMockModule(filePath);
      const nextConfig = config ? { ...info.config, ...config } : info.config;

      await writeMockFile(filePath, {
        headerComment: info.headerComment,
        config: nextConfig,
        description: info.description,
      });
      updated++;
    } catch (err) {
      errors.push(`Failed to update ${key}: ${err instanceof Error ? err.message : String(err)}`);
    }
  }

  sendJson(res, { success: true, updated, errors: errors.length > 0 ? errors : undefined });
  return true;
}

const API_ROUTES: Record<string, ApiHandler> = {
  list: handleList,
  detail: handleDetail,
  update: handleUpdate,
  "batch-update": handleBatchUpdate,
  create: handleCreate,
  delete: handleDelete,
};

async function handleInspectorApi(ctx: InspectorApiContext): Promise<void> {
  try {
    const mockFileMap = ctx.getMockFileMap();
    const handler = API_ROUTES[ctx.pathname];
    if (handler) {
      await handler(ctx, mockFileMap);
      return;
    }
    sendJson(ctx.res, { error: "Unknown inspector endpoint" }, 404);
  } catch (error) {
    console.error("[automock] Inspector request failed:", error);
    sendJson(ctx.res, { error: "Inspector failed" }, 500);
  }
}
